<template>
  <v-dialog
    :value="value"
    :fullscreen="fullscreen"
    :max-width="maxWidth"
    persistent
    :hide-overlay="fullscreen"
    transition="dialog-bottom-transition"
    @input="$emit('input', $event)"
  >
    <v-card
      v-if="value && form"
      :tile="fullscreen"
      class="idtr-dialog"
      :class="{ 'idtr-dialog--windowed': !fullscreen }"
    >
      <v-toolbar flat dense class="idtr-dialog__toolbar">
        <v-toolbar-title class="title">{{ title }}</v-toolbar-title>
        <v-spacer />
        <v-text-field
          v-model.number="form.blankPages"
          type="number"
          min="0"
          max="10"
          label="Blank pages"
          dense
          outlined
          hide-details
          class="mr-3 flex-grow-0"
          style="min-width: 140px; max-width: 140px"
        />
        <secondary-button class="mr-3" @click="addAccount"
          >Add account</secondary-button
        >
        <text-button class="mr-3" :disabled="generating" @click="close"
          >Close</text-button
        >
        <primary-button :loading="generating" @click="download"
          >Download PDF</primary-button
        >
      </v-toolbar>
      <identity-theft-report-editor
        ref="editor"
        :form="form"
        :template-def="templateDef"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";
import SecondaryButton from "@/components/ui/buttons/SecondaryButton.vue";
import TextButton from "@/components/ui/buttons/TextButton.vue";
import IdentityTheftReportEditor from "./IdentityTheftReportEditor.vue";
import {getIdtrTemplate} from "@/assets/constants/identity.theft.report.constants";
import {
  base64PdfToFile,
  createAccount,
  createForm,
  downloadBase64Pdf,
  mergePrefill,
} from "@/assets/utils/identity.theft.report.utils";

const MAX_WIDTH_PX = 1000;
const FULLSCREEN_BELOW_PX = 1100;

/**
 * Identity Theft Report editor in a dialog (full screen on narrow windows), prefilled by the caller.
 * Each time it opens, the form is rebuilt from `templateKey` + `prefill`.
 * After a download emits `generated` with the PDF as a File and closes.
 */
export default {
  components: {
    PrimaryButton,
    SecondaryButton,
    TextButton,
    IdentityTheftReportEditor,
  },
  props: {
    value: {type: Boolean, default: false},
    templateKey: {type: String, default: ""},
    prefill: {type: Object, default: null},
  },
  data() {
    return {
      form: null,
      templateDef: null,
      generating: false,
    };
  },
  computed: {
    // Full screen only where a windowed dialog would not fit the sheet and the toolbar.
    fullscreen() {
      return this.$vuetify.breakpoint.width < FULLSCREEN_BELOW_PX;
    },
    maxWidth() {
      return this.fullscreen ? undefined : `${MAX_WIDTH_PX}px`;
    },
    title() {
      return this.templateDef
        ? `Identity Theft Report: ${this.templateDef.name}`
        : "Identity Theft Report";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) this.init();
      },
    },
  },
  methods: {
    init() {
      this.templateDef = getIdtrTemplate(this.templateKey);
      this.form = this.templateDef
        ? mergePrefill(createForm(this.templateDef.key), this.prefill)
        : null;
    },
    close() {
      this.$emit("input", false);
    },
    addAccount() {
      this.form.accounts.push(createAccount());
    },
    async download() {
      this.generating = true;
      this.$loader.show();
      try {
        const result = await this.$refs.editor.generatePdf();
        if (!result) return;
        downloadBase64Pdf(result.pdf, result.fileName);
        this.$emit("generated", base64PdfToFile(result.pdf, result.fileName));
        this.close();
      } catch (error) {
        this.$toast.error(error.message || "Failed to generate PDF");
      } finally {
        this.$loader.hide();
        this.generating = false;
      }
    },
  },
};
</script>

<style lang="scss">
.idtr-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;
  &--windowed {
    // Below the 90% max-height of a windowed v-dialog, so only the sheets scroll.
    height: 88vh;
    overflow: hidden;
  }
  &__toolbar {
    flex: 0 0 auto;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  }
  // Only the sheets scroll: the editor fills the space under the toolbar.
  .idtr-editor {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .idtr-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }
}
</style>
